import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDLineNumber } from '../primitives/CDLineNumber';

export interface ClosingCostPayment {
  atClosing?: number;
  beforeClosing?: number;
}

export interface ClosingCostLineItemData {
  lineNumber: number | string;
  description: string;
  toEntity?: string; // e.g., "to ABC Title Company"
  borrowerPaid?: ClosingCostPayment;
  sellerPaid?: ClosingCostPayment;
  paidByOthers?: number;
}

export interface CDClosingCostLineItemProps {
  /** Line item data */
  data: ClosingCostLineItemData;
  /** Whether to show all 5 columns (Borrower at/before, Seller at/before, Others) */
  showAllColumns?: boolean;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: ClosingCostLineItemData) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDClosingCostLineItem - Single line item row for closing costs
 *
 * Used in Loan Costs and Other Costs sections.
 * Can show 2-column (Borrower/Seller) or 5-column layout.
 */
export const CDClosingCostLineItem = React.forwardRef<
  HTMLTableRowElement,
  CDClosingCostLineItemProps
>(({ data, showAllColumns = true, editable = false, onChange, className }, ref) => {
  // Helper to update a field
  const update = (updates: Partial<ClosingCostLineItemData>) => {
    onChange?.({ ...data, ...updates });
  };

  // Helper to update borrower payment
  const updateBorrowerPaid = (field: keyof ClosingCostPayment, value: number) => {
    onChange?.({
      ...data,
      borrowerPaid: { ...data.borrowerPaid, [field]: value },
    });
  };

  // Helper to update seller payment
  const updateSellerPaid = (field: keyof ClosingCostPayment, value: number) => {
    onChange?.({
      ...data,
      sellerPaid: { ...data.sellerPaid, [field]: value },
    });
  };

  return (
    <tr ref={ref} className={cn('cd-text-body', className)}>
      {/* Line Number */}
      <td className="border border-cd-black px-1 py-0.5 w-6 text-right align-top">
        <CDLineNumber number={data.lineNumber} />
      </td>

      {/* Description */}
      <td className="border border-cd-black px-1 py-0.5 align-top">
        <CDEditableText
          value={data.description}
          editable={editable}
          onChange={(v) => update({ description: v })}
          size="small"
        />
        {(data.toEntity || editable) && (
          <CDEditableText
            value={data.toEntity}
            editable={editable}
            onChange={(v) => update({ toEntity: v })}
            size="tiny"
            placeholder="to..."
            className="text-cd-gray-text"
          />
        )}
      </td>

      {showAllColumns ? (
        <>
          {/* Borrower-Paid At Closing */}
          <td className="border border-cd-black px-1 py-0.5 text-right w-16 align-top">
            <CDCurrency
              value={data.borrowerPaid?.atClosing}
              editable={editable}
              onChange={(v) => updateBorrowerPaid('atClosing', v)}
              placeholder=""
              className="cd-text-small"
            />
          </td>

          {/* Borrower-Paid Before Closing */}
          <td className="border border-cd-black px-1 py-0.5 text-right w-16 align-top">
            <CDCurrency
              value={data.borrowerPaid?.beforeClosing}
              editable={editable}
              onChange={(v) => updateBorrowerPaid('beforeClosing', v)}
              placeholder=""
              className="cd-text-small"
            />
          </td>

          {/* Seller-Paid At Closing */}
          <td className="border border-cd-black px-1 py-0.5 text-right w-16 align-top">
            <CDCurrency
              value={data.sellerPaid?.atClosing}
              editable={editable}
              onChange={(v) => updateSellerPaid('atClosing', v)}
              placeholder=""
              className="cd-text-small"
            />
          </td>

          {/* Seller-Paid Before Closing */}
          <td className="border border-cd-black px-1 py-0.5 text-right w-16 align-top">
            <CDCurrency
              value={data.sellerPaid?.beforeClosing}
              editable={editable}
              onChange={(v) => updateSellerPaid('beforeClosing', v)}
              placeholder=""
              className="cd-text-small"
            />
          </td>

          {/* Paid by Others */}
          <td className="border border-cd-black px-1 py-0.5 text-right w-16 align-top">
            <CDCurrency
              value={data.paidByOthers}
              editable={editable}
              onChange={(v) => update({ paidByOthers: v })}
              placeholder=""
              className="cd-text-small"
            />
          </td>
        </>
      ) : (
        <>
          {/* Simplified 2-column: Borrower / Seller */}
          <td className="border border-cd-black px-1 py-0.5 text-right w-20 align-top">
            <CDCurrency
              value={data.borrowerPaid?.atClosing}
              editable={editable}
              onChange={(v) => updateBorrowerPaid('atClosing', v)}
              placeholder=""
              className="cd-text-small"
            />
          </td>
          <td className="border border-cd-black px-1 py-0.5 text-right w-20 align-top">
            <CDCurrency
              value={data.sellerPaid?.atClosing}
              editable={editable}
              onChange={(v) => updateSellerPaid('atClosing', v)}
              placeholder=""
              className="cd-text-small"
            />
          </td>
        </>
      )}
    </tr>
  );
});

CDClosingCostLineItem.displayName = 'CDClosingCostLineItem';

/**
 * CDClosingCostSubtotal - Subtotal row for a section
 */
export interface CDClosingCostSubtotalProps {
  label: string;
  borrowerTotal?: number;
  sellerTotal?: number;
  showAllColumns?: boolean;
  className?: string;
}

export const CDClosingCostSubtotal = React.forwardRef<
  HTMLTableRowElement,
  CDClosingCostSubtotalProps
>(({ label, borrowerTotal, sellerTotal, showAllColumns = true, className }, ref) => {
  return (
    <tr ref={ref} className={cn('cd-text-small font-bold bg-cd-gray-light', className)}>
      <td className="border border-cd-black px-1 py-0.5" colSpan={2}>
        {label}
      </td>
      {showAllColumns ? (
        <>
          <td className="border border-cd-black px-1 py-0.5 text-right">
            <CDCurrency value={borrowerTotal} className="cd-text-small" />
          </td>
          <td className="border border-cd-black px-1 py-0.5"></td>
          <td className="border border-cd-black px-1 py-0.5 text-right">
            <CDCurrency value={sellerTotal} className="cd-text-small" />
          </td>
          <td className="border border-cd-black px-1 py-0.5"></td>
          <td className="border border-cd-black px-1 py-0.5"></td>
        </>
      ) : (
        <>
          <td className="border border-cd-black px-1 py-0.5 text-right">
            <CDCurrency value={borrowerTotal} className="cd-text-small" />
          </td>
          <td className="border border-cd-black px-1 py-0.5 text-right">
            <CDCurrency value={sellerTotal} className="cd-text-small" />
          </td>
        </>
      )}
    </tr>
  );
});

CDClosingCostSubtotal.displayName = 'CDClosingCostSubtotal';
